import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { T } from "./theme";
import {
  RefreshCw, User, LogOut, ChevronRight, ChevronDown,
  Check, AlertCircle,
} from "lucide-react";

const PAGE_TITLES = {
  '/':              'Dashboard',
  '/depoimentos':   'Depoimentos',
  '/comparativo':   'Comparativo',
  '/benchmarking':  'Benchmarking',
  '/rankings':      'Rankings',
  '/tips':          'Tips CXO',
  '/pdsa':          'PDSA',
  '/qualidade':     'Qualidade IA',
  '/admin':         'Admin',
  '/crm':           'CRM',
  '/configuracoes': 'Configurações',
};

function getInitials(nome) {
  if (!nome) return "?";
  const parts = nome.replace(/^Dr\.\s*/i, "").split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return nome.slice(0, 2).toUpperCase();
}

const ROLE_COLORS = {
  admin:  { bg: "rgba(99,102,241,0.10)",  color: "#6366F1" },
  cxo:    { bg: "rgba(139,92,246,0.10)",  color: "#8B5CF6" },
  gestor: { bg: "rgba(20,184,166,0.10)",  color: "#14B8A6" },
  viewer: { bg: "rgba(156,163,175,0.10)", color: "#9CA3AF" },
};

const SYNC_OPTIONS = [
  { value: "mes",      label: "Este Mês" },
  { value: "semestre", label: "Últimos 6 Meses" },
  { value: "ano",      label: "Este Ano" },
  { value: "todos",    label: "Tudo" },
];

export default function Header({ session, onLogout }) {
  const [syncing, setSyncing]           = useState(false);
  const [syncStatus, setSyncStatus]     = useState(null); // null | "ok" | "error"
  const [syncMsg, setSyncMsg]           = useState("");
  const [dropOpen, setDropOpen]         = useState(false);
  const [syncMenuOpen, setSyncMenuOpen] = useState(false);
  const dropRef  = useRef(null);
  const syncRef  = useRef(null);
  const location = useLocation();

  const usuario  = session?.usuario || session || {};
  const initials = getInitials(usuario.nome || "");
  const role     = usuario.role || "viewer";
  const unidade  = usuario.unidade || "";
  const pageTitle = PAGE_TITLES[location.pathname] || "TrueNPS";
  const roleStyle = ROLE_COLORS[role] || ROLE_COLORS.viewer;

  useEffect(() => {
    function onClickOutside(e) {
      if (dropRef.current  && !dropRef.current.contains(e.target))  setDropOpen(false);
      if (syncRef.current  && !syncRef.current.contains(e.target))  setSyncMenuOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const sync = async (periodo = "mes") => {
    setSyncing(true); setSyncStatus(null); setSyncMsg("Sincronizando..."); setSyncMenuOpen(false);
    try {
      const es = new EventSource(`/api/sync?periodo=${periodo}`);
      es.onmessage = e => {
        const d = JSON.parse(e.data);
        setSyncMsg(d.message || "");
        if (d.done) {
          es.close(); setSyncing(false);
          setSyncStatus("ok"); setSyncMsg("Sincronizado");
          setTimeout(() => { setSyncStatus(null); setSyncMsg(""); window.location.reload(); }, 2000);
        }
      };
      es.onerror = () => {
        es.close(); setSyncing(false);
        setSyncStatus("error"); setSyncMsg("Erro ao sincronizar");
        setTimeout(() => { setSyncStatus(null); setSyncMsg(""); }, 3000);
      };
    } catch { setSyncing(false); }
  };

  const handleLogout = async () => {
    setDropOpen(false);
    if (onLogout) { await onLogout(); return; }
    await fetch("/api/logout", { method: "POST", credentials: "include" });
    window.location.reload();
  };

  return (
    <div style={{
      height: 80, // Taller header for more breathing room
      background: T.bgCard,
      borderBottom: `1px solid ${T.border}`,
      display: "flex", alignItems: "center",
      padding: "0 32px", gap: 24, flexShrink: 0,
      position: "relative", zIndex: 50,
    }}>
      {/* Breadcrumb / Title */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: T.textLow, minWidth: 0 }}>
        {unidade && (
          <>
            <span style={{ color: T.textMid, fontWeight: 500 }}>{unidade}</span>
            <ChevronRight size={14} style={{ color: T.border, flexShrink: 0 }}/>
          </>
        )}
        <span style={{ color: T.textHi, fontWeight: 700, fontSize: 16 }}>{pageTitle}</span>

        {/* Sync status inline */}
        {syncMsg && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            marginLeft: 12, padding: "4px 10px", borderRadius: 99,
            fontSize: 12, fontWeight: 600,
            background: syncStatus === "ok" ? "rgba(16,185,129,0.10)" : syncStatus === "error" ? "rgba(239,68,68,0.10)" : T.accentDim,
            color: syncStatus === "ok" ? T.green : syncStatus === "error" ? T.red : T.accent,
          }}>
            {syncStatus === "ok" && <Check size={12} strokeWidth={3}/>}
            {syncStatus === "error" && <AlertCircle size={12} strokeWidth={3}/>}
            {syncMsg}
          </span>
        )}
      </div>

      {/* Quick Actions & Profile */}
      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
        {/* Sync split-button */}
        <div ref={syncRef} style={{ position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", background: T.bgInput, borderRadius: 10, padding: 4 }}>
            <button
              onClick={() => sync("mes")}
              disabled={syncing}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 14px", borderRadius: 8,
                background: "transparent",
                border: "none",
                color: syncing ? T.accent : T.textMid,
                cursor: syncing ? "not-allowed" : "pointer",
                fontSize: 13, fontWeight: 600,
                transition: "all 0.2s ease", fontFamily: T.fontBody,
              }}
              onMouseEnter={e => { if (!syncing) { e.currentTarget.style.background = T.bgCard; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)"; e.currentTarget.style.color = T.textHi; } }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.color = T.textMid; }}
            >
              <RefreshCw size={14} style={{ animation: syncing ? "spin 1s linear infinite" : "none" }}/>
              {syncing ? "Sincronizando..." : "Atualizar"}
            </button>
            <div style={{ width: 1, height: 16, background: T.border, margin: "0 2px" }} />
            <button
              onClick={() => !syncing && setSyncMenuOpen(o => !o)}
              disabled={syncing}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "8px", borderRadius: 8,
                background: "transparent",
                border: "none",
                color: T.textMid, cursor: syncing ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={e => { if (!syncing) { e.currentTarget.style.background = T.bgCard; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)"; e.currentTarget.style.color = T.textHi; } }}
              onMouseLeave={e => { if (!syncing) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.color = T.textMid; } }}
            >
              <ChevronDown size={14} strokeWidth={2.5}/>
            </button>
          </div>

          {syncMenuOpen && !syncing && (
            <div style={{
              position: "absolute", top: "calc(100% + 8px)", right: 0,
              minWidth: 180,
              background: T.bgCard,
              border: `1px solid ${T.border}`,
              borderRadius: T.radius,
              boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
              overflow: "hidden", zIndex: 100,
              padding: "4px",
            }}>
              <div style={{ padding: "10px 12px 6px", fontSize: 10, color: T.textLow, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
                Sincronizar Dados
              </div>
              {SYNC_OPTIONS.map(({ value, label }) => (
                <button key={value} onClick={() => sync(value)} style={{
                  width: "100%", display: "block", padding: "10px 12px",
                  background: "transparent", border: "none", borderRadius: 8,
                  color: T.textHi, fontSize: 13, fontWeight: 500, cursor: "pointer",
                  textAlign: "left", transition: "all 0.2s ease", fontFamily: T.fontBody,
                }}
                onMouseEnter={e => e.currentTarget.style.background = T.bgBase}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Separator */}
        <div style={{ width: 1, height: 24, background: T.border, flexShrink: 0 }}/>

        {/* Avatar */}
        <div ref={dropRef} style={{ position: "relative" }}>
          <button
            onClick={() => setDropOpen(o => !o)}
            style={{
              width: 40, height: 40, borderRadius: 12, // Squircle profile picture
              background: T.gradientAccent,
              border: `2px solid ${dropOpen ? T.accent : "transparent"}`,
              color: "#fff", fontSize: 14, fontWeight: 700,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.2s ease", letterSpacing: "0.05em", flexShrink: 0,
              boxShadow: dropOpen ? `0 0 0 3px ${T.accentDim}` : "none",
            }}
            title={usuario.nome || ""}
          >
            {initials}
          </button>

          {dropOpen && (
            <div style={{
              position: "absolute", top: "calc(100% + 12px)", right: 0,
              width: 260,
              background: T.bgCard,
              border: `1px solid ${T.border}`,
              borderRadius: T.radius,
              boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
              zIndex: 100,
              padding: 6,
            }}>
              {/* User info */}
              <div style={{ padding: "16px", borderBottom: `1px solid ${T.border}`, marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 14,
                    background: T.gradientAccent,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 16, fontWeight: 800, color: "#fff", flexShrink: 0,
                  }}>
                    {initials}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.textHi, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {usuario.nome || "Usuário"}
                    </div>
                    <div style={{ fontSize: 12, color: T.textLow, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 500 }}>
                      {usuario.email || ""}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {unidade && (
                    <span style={{ fontSize: 12, color: T.textMid, fontWeight: 500, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {unidade}
                    </span>
                  )}
                  <span style={{
                    padding: "4px 10px", borderRadius: 99,
                    background: roleStyle.bg, color: roleStyle.color,
                    fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em",
                    flexShrink: 0,
                  }}>
                    {role}
                  </span>
                </div>
              </div>

              {/* Actions */}
              {[
                { icon: User, label: "Meu Perfil", onClick: () => setDropOpen(false) },
              ].map(({ icon: Icon, label, onClick }) => (
                <button key={label} onClick={onClick} style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 14px", background: "transparent", border: "none", borderRadius: 12,
                  color: T.textHi, fontSize: 14, fontWeight: 500, cursor: "pointer",
                  textAlign: "left", transition: "all 0.2s ease", fontFamily: T.fontBody,
                }}
                onMouseEnter={e => e.currentTarget.style.background = T.bgBase}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <Icon size={16} strokeWidth={2} style={{ color: T.textLow }}/>
                  {label}
                </button>
              ))}

              <div style={{ borderTop: `1px solid ${T.border}`, margin: "6px 0" }}/>

              <button onClick={handleLogout} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12,
                padding: "12px 14px", background: "transparent", border: "none", borderRadius: 12,
                color: T.red, fontSize: 14, fontWeight: 600, cursor: "pointer",
                textAlign: "left", transition: "all 0.2s ease", fontFamily: T.fontBody,
              }}
              onMouseEnter={e => e.currentTarget.style.background = `rgba(239,68,68,0.06)`}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <LogOut size={16} strokeWidth={2.5}/>
                Sair
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

